import type { Session, SupabaseClient } from "@supabase/supabase-js";

/** Brief pause so the browser commits Set-Cookie from sync-session before hard navigation. */
const POST_SYNC_SETTLE_MS = 175;

/**
 * Mirrors the browser (localStorage) session into Next.js auth cookies so
 * middleware and API routes using createServerClient() still see the user.
 * @returns whether the server accepted the sync (caller may retry or abort navigation).
 */
export async function syncSupabaseSessionToServer(session: Session | null): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!session?.access_token || !session.refresh_token) return false;

  try {
    const res = await fetch("/api/auth/sync-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }),
    });
    if (!res.ok) {
      console.warn("[auth] sync-session failed:", res.status, await res.text());
      return false;
    }
    return true;
  } catch (e) {
    console.warn("[auth] sync-session error:", e);
    return false;
  }
}

/**
 * After password/OAuth sign-in: sync cookies, flush client session state, then yield so
 * the first navigation sees the same session server-side (avoids "first login" bounce to /login).
 */
export async function prepareServerSessionAfterSignIn(
  supabase: SupabaseClient,
  session: Session | null
): Promise<void> {
  if (!session?.access_token || !session.refresh_token) {
    throw new Error("No session to sync");
  }
  const synced = await syncSupabaseSessionToServer(session);
  if (!synced) {
    throw new Error("Could not sync session. Please try again.");
  }
  await supabase.auth.getSession();
  await new Promise((r) => setTimeout(r, POST_SYNC_SETTLE_MS));
}

/**
 * After OAuth/PKCE, the server sets httpOnly-style cookies but the browser client
 * uses localStorage — hydrate the JS client from the cookie session once.
 */
export async function bootstrapBrowserSessionFromServerCookies(): Promise<{
  access_token: string;
  refresh_token: string;
  expires_at?: number;
} | null> {
  if (typeof window === "undefined") return null;
  try {
    const res = await fetch("/api/auth/bootstrap-session", { credentials: "same-origin" });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
    };
    if (!data.access_token || !data.refresh_token) return null;
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
    };
  } catch {
    return null;
  }
}
